import { LABEL_COLORS, MODE_TYPES, SETTINGS } from '@aytm/app/stats/page/question-views/psc/lib/constants'
import { measureTextWidth } from '@aytm/app/stats/lib/util/dom'

export const pct = (v, decimals = 1) => `${Math.abs(v || 0).toFixed(decimals)}%`
// Intensity is a scale magnitude rather than a share of respondents, so it carries no
// percent sign -- the same discrete/intensity split the items-list rows follow.
export const magnitude = v => Math.abs(v || 0).toFixed(1)
// export const ellipsize = (s, max = 18) => (s?.length > max ? s.slice(0, max - 1) + '…' : s || '')

export const sum = arr => (arr || []).reduce((acc, cur) => acc + cur, 0)

export const mean = values => {
  if (!values?.length) return null

  return values.reduce((s, x) => s + x, 0) / values.length
}
export const meanW = (mus, weights) => {
  let nom = 0
  let den = 0

  for (const [i, element] of mus.entries()) {
    nom += (weights[i] || 0) * (element || 0)
    den += weights[i] || 0
  }

  return den ? nom / den : 0
}

export const median = values => {
  if (!values?.length) return null

  const v = [...values].sort((a, b) => a - b)
  const h = Math.floor(v.length / 2)

  return v.length % 2 ? v[h] : (v[h - 1] + v[h]) / 2
}
export const medianW = (values, weights) => {
  if (!values?.length) return null

  const half = sum(weights) / 2
  const list = values
    .map((v, i) => {
      return { v, w: weights[i] }
    })
    .sort((a, b) => a.v - b.v)

  let acc = 0
  let idx = 0
  for (const [i, element] of list.entries()) {
    acc += element.w
    if (acc > half) {
      idx = i
      break
    }
  }

  return list.length % 2 ? list[idx].v : ((list[idx - 1] ?? list[idx]).v + list[idx].v) / 2
}

export const getLabelsColor = ({ mode, data: chartData }) => {
  if (mode !== MODE_TYPES.pda && mode !== MODE_TYPES.pdm) {
    return {
      leftColor: LABEL_COLORS.default,
      rightColor: LABEL_COLORS.default
    }
  }

  const data = mode === MODE_TYPES.pda ? chartData.intensity.deltas.avg : chartData.intensity.deltas.mean

  return {
    leftColor: 0 <= data.percents ? LABEL_COLORS.disabled : LABEL_COLORS.default,
    rightColor: 0 >= data.percents ? LABEL_COLORS.disabled : LABEL_COLORS.default
  }
}

export const parseText = text => {
  if (Array.isArray(text)) return text
  try {
    const parsed = JSON.parse(text)
    return Array.isArray(parsed) ? parsed : ['', '']
  } catch {
    return ['', '']
  }
}

export const calcChartData = ({ answer, batches, degree = 201, neutral = 0 }) => {
  const chartData = []

  const power = Math.floor(degree / 2) / 100

  for (const b of batches) {
    const text = parseText(b.text)
    const data = {
      popularity: [
        {
          amount: 0,
          percents: 0,
          text: text[0]
        },
        {
          amount: 0,
          percents: 0,
          text: 'Neutral'
        },
        {
          amount: 0,
          percents: 0,
          text: text[1]
        }
      ],
      intensity: {
        pairs: [
          {
            mean: {
              points: 0,
              amount: 0,
              percents: 0,
              text: text[0]
            },
            avg: {
              points: 0,
              amount: 0,
              percents: 0,
              text: text[0]
            }
          },
          {
            mean: {
              points: 0,
              amount: 0,
              percents: 0,
              text: text[1]
            },
            avg: {
              points: 0,
              amount: 0,
              percents: 0,
              text: text[1]
            }
          }
        ],
        deltas: {
          mean: {
            points: 0,
            amount: 0,
            percents: 0,
            text: text[0]
          },
          avg: {
            points: 0,
            amount: 0,
            percents: 0,
            text: text[0]
          }
        }
      }
    }

    if (null !== answer && 'loading' != answer) {
      const a = Object.assign({ values: [], weights: null }, answer[`b${b.position}`] || {})

      const values = [[], [], []]
      const weights = [[], [], []]

      for (let i = 0; i < a.values.length; i++) {
        if (a.values[i] == undefined) continue

        if (a.values[i] < -neutral) {
          values[0].push(a.values[i])
          if (null !== a.weights) {
            weights[0].push(a.weights[i] || 0)
          }
        }
        if (a.values[i] >= -neutral && a.values[i] <= neutral) {
          values[1].push(a.values[i])
          if (null !== a.weights) {
            weights[1].push(a.weights[i] || 0)
          }
        }
        if (a.values[i] > neutral) {
          values[2].push(a.values[i])
          if (null !== a.weights) {
            weights[2].push(a.weights[i] || 0)
          }
        }
      }

      for (const i of [0, 1, 2]) {
        data.popularity[i].amount = null !== a.weights ? sum(weights[i]) : values[i].length
      }

      const total = [0, 1, 2].reduce((acc, cur) => acc + data.popularity[cur].amount, 0)
      if (0 < total) {
        for (const i of [0, 1, 2]) {
          // unrounded: the share is printed at the report's percentage precision wherever it is read
          data.popularity[i].percents = (data.popularity[i].amount * 100) / total
        }
      }

      for (const i of [0, 1]) {
        if (null !== a.weights) {
          data.intensity.pairs[i].mean.points = Math.round(medianW(values[i * 2], weights[i * 2]) * 100) / 100
          data.intensity.pairs[i].mean.amount = sum(weights[i * 2])
        } else {
          data.intensity.pairs[i].mean.points = Math.round(median(values[i * 2]) * 100) / 100
          data.intensity.pairs[i].mean.amount = values[i * 2].length
        }

        data.intensity.pairs[i].mean.text = text[i]
        data.intensity.pairs[i].mean.percents =
          Math.round(Math.abs(data.intensity.pairs[i].mean.points / power) * 100) / 100

        if (null !== a.weights) {
          data.intensity.pairs[i].avg.points = Math.round(meanW(values[i * 2], weights[i * 2]) * 100) / 100
          data.intensity.pairs[i].avg.amount = sum(weights[i * 2])
        } else {
          data.intensity.pairs[i].avg.points = Math.round(mean(values[i * 2]) * 100) / 100
          data.intensity.pairs[i].avg.amount = values[i * 2].length
        }

        data.intensity.pairs[i].avg.text = text[i]
        data.intensity.pairs[i].avg.percents =
          Math.round(Math.abs(data.intensity.pairs[i].avg.points / power) * 100) / 100
      }

      if (null !== a.weights) {
        const vals = values[0].concat(values[2])
        const wgts = weights[0].concat(weights[2])

        data.intensity.deltas.mean.points = Math.round(medianW(vals, wgts) * 100) / 100
        data.intensity.deltas.mean.amount = sum(wgts)
        data.intensity.deltas.mean.percents = data.intensity.deltas.mean.points / power

        data.intensity.deltas.avg.points = Math.round(meanW(vals, wgts) * 100) / 100
        data.intensity.deltas.avg.amount = sum(wgts)
      } else {
        data.intensity.deltas.mean.points = Math.round(median(values[0].concat(values[2])) * 100) / 100
        data.intensity.deltas.mean.amount = values[0].length + values[2].length
        data.intensity.deltas.mean.percents = data.intensity.deltas.mean.points / power

        data.intensity.deltas.avg.points = Math.round(mean(values[0].concat(values[2])) * 100) / 100
        data.intensity.deltas.avg.amount = values[0].length + values[2].length
      }

      if (0 > data.intensity.deltas.mean.percents) {
        data.intensity.deltas.mean.text = text[0]
      }
      if (0 == data.intensity.deltas.mean.percents) {
        data.intensity.deltas.mean.text = 'Neutral'
      }
      if (0 < data.intensity.deltas.mean.percents) {
        data.intensity.deltas.mean.text = text[1]
      }

      data.intensity.deltas.avg.percents = data.intensity.deltas.avg.points / power
      if (0 > data.intensity.deltas.avg.percents) {
        data.intensity.deltas.avg.text = text[0]
      }
      if (0 == data.intensity.deltas.avg.percents) {
        data.intensity.deltas.avg.text = 'Neutral'
      }
      if (0 < data.intensity.deltas.avg.percents) {
        data.intensity.deltas.avg.text = text[1]
      }
    }

    chartData.push(data)
  }

  return chartData
}

const generateDiscrete = ({ width, data: chartData, percentDecimals }) => {
  const data = chartData.popularity
  const divider = data[1].percents ? SETTINGS.barMargin : SETTINGS.barMargin / 2

  const w1 = !data[0].percents ? 0 : divider / 2
  const w2 = !data[2].percents ? 0 : divider / 2

  const WIDTH = (data[0].percents * (width - w1 - w2 - 2 * (SETTINGS.poleLabelWidth + SETTINGS.poleLabelGap))) / 100
  const NWIDTH = (data[1].percents * (width - w1 - w2 - 2 * (SETTINGS.poleLabelWidth + SETTINGS.poleLabelGap))) / 100
  const RWIDTH = (data[2].percents * (width - w1 - w2 - 2 * (SETTINGS.poleLabelWidth + SETTINGS.poleLabelGap))) / 100

  // Lay out segments separated by the adaptive spacers w1/w2 so the five widths
  // (left, left spacer, neutral, right spacer, right) sum exactly to the working area.
  return [
    {
      x: 0,
      w: WIDTH,
      title: data[0].text,
      value: `${pct(data[0].percents, percentDecimals)} (${data[0].amount})`
    },
    {
      x: WIDTH + w1,
      w: NWIDTH,
      title: data[1].text,
      value: `${pct(data[1].percents, percentDecimals)} (${data[1].amount})`
    },
    {
      x: WIDTH + w1 + NWIDTH + w2,
      w: RWIDTH,
      title: data[2].text,
      value: `${pct(data[2].percents, percentDecimals)} (${data[2].amount})`
    }
  ]
}

// The three shapes a bar is drawn from, and the id one of them shares with the table row that names
// it -- the pair find each other by that id when either is hovered.
export const POLE_LEFT = 'l'
export const POLE_CENTER = 'c'
export const POLE_RIGHT = 'r'

export const pointIdOf = (position, pole) => `b${position}${pole}`

// Which pole a delta is drawn at: it leans left when negative, and stands right otherwise -- so a
// delta of nothing has a side to belong to even though it draws no width.
export const deltaPole = percents => (0 > percents ? POLE_LEFT : POLE_RIGHT)

const generatePowerDelta = ({ data: chartData, mode, width }) => {
  const data = mode === MODE_TYPES.pda ? chartData.intensity.deltas.avg : chartData.intensity.deltas.mean

  const calculatedWidth = (width - 2 * (SETTINGS.poleLabelWidth + SETTINGS.poleLabelGap) - SETTINGS.barMargin) / 2
  const absoluteWidth = Math.abs(data.percents * calculatedWidth) / 100
  const left = POLE_LEFT === deltaPole(data.percents)

  return [
    {
      x: left ? calculatedWidth - absoluteWidth : calculatedWidth,
      w: left ? absoluteWidth : 0,
      title: left ? data.text : '',
      value: `${magnitude(data.percents)} (${data.amount})`
    },
    {
      x: calculatedWidth,
      w: SETTINGS.barMargin,
      title: '',
      value: ''
    },
    {
      x: calculatedWidth + SETTINGS.barMargin,
      w: left ? 0 : absoluteWidth,
      title: data.text,
      value: `${magnitude(data.percents)} (${data.amount})`
    }
  ]
}

const generatePowerBoth = ({ data: chartData, mode, width }) => {
  const data = chartData.intensity.pairs.map(pair => pair[mode === MODE_TYPES.pba ? 'avg' : 'mean'])
  const calculatedWidth = (width - 2 * (SETTINGS.poleLabelWidth + SETTINGS.poleLabelGap) - SETTINGS.barMargin) / 2

  const leftValue = data[0]
  const leftWidth = (leftValue.percents * calculatedWidth) / 100

  const rightValue = data[1]
  const rightWidth = (rightValue.percents * calculatedWidth) / 100

  return [
    {
      x: calculatedWidth - leftWidth,
      w: leftWidth,
      title: leftValue.text,
      value: magnitude(leftValue.percents)
    },
    {
      x: calculatedWidth,
      w: SETTINGS.barMargin,
      title: '',
      value: ''
    },
    {
      x: calculatedWidth + SETTINGS.barMargin,
      w: rightWidth,
      title: rightValue.text,
      value: magnitude(rightValue.percents)
    }
  ]
}

export const generateRowData = ({ width, mode, data, percentDecimals }) => {
  if (mode === MODE_TYPES.d) {
    return generateDiscrete({ width, data, percentDecimals })
  }

  if (mode === MODE_TYPES.pda || mode === MODE_TYPES.pdm) {
    return generatePowerDelta({ width, mode, data })
  }

  if (mode === MODE_TYPES.pba || mode === MODE_TYPES.pbm) {
    return generatePowerBoth({ width, mode, data })
  }

  return []
}

export const stripTags = (s = '') => String(s).replace(/<[^>]*>/g, '')

const LABEL_FONT_SIZE = 14
const LABEL_FONT = `${LABEL_FONT_SIZE}px Ubuntu, Calibri, Segoe, Helvetica, Arial`
const LABEL_TRUNCATE_CHARS = 13

// Approximate the rendered width of a pole-label string at the chart's label font.
const measureLabel = text => measureTextWidth(text, LABEL_FONT) ?? (text || '').length * LABEL_FONT_SIZE * 0.5

// Fit a pole label into at most two lines within maxWidth (math sub-spec §11):
// greedy word wrap; a first word that alone overflows is truncated to a fixed
// length plus an ellipsis; words that still do not fit after the second line are dropped.
export const fitLabel = (text, maxWidth) => {
  const full = text || ''
  const words = full.split(/\s+/).filter(Boolean)
  if (!words.length) return { lines: [], full, truncated: false }

  if (measureLabel(words[0]) > maxWidth) {
    return { lines: [`${words[0].slice(0, LABEL_TRUNCATE_CHARS)}…`], full, truncated: true }
  }

  const lines = []
  let i = 0
  while (lines.length < 2 && i < words.length) {
    let line = words[i]
    i += 1
    while (i < words.length && measureLabel(`${line} ${words[i]}`) <= maxWidth) {
      line += ` ${words[i]}`
      i += 1
    }
    lines.push(line)
  }

  return { lines, full, truncated: false }
}
